import commaNumber from 'comma-number';
import {
  type ComponentType,
  type CSSProperties,
  type HTMLAttributes,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { Flex, HorizontalScrollView } from '../base';
import Typo from '../Typography/Typo';
import TabsContext from './TabContext';
import type { Tab } from './types';

type TabCustomT = {
  tabSize?: 's' | 'm';
  tabColor?: 'default' | 'primary';
  scrollEnabled?: boolean;
};

type TabHeaderProps = {
  style?: CSSProperties;
  tabStyle?: ComponentType<TabItemStyleProps & HTMLAttributes<HTMLDivElement>>;
  onSelect?: (tabId: StringNumberValue | null) => void;
} & TabCustomT;

const TabHeader = ({
  scrollEnabled = true,
  tabColor = 'default',
  tabSize = 's',
  style,
  tabStyle,
  onSelect,
}: TabHeaderProps) => {
  const { tabs, selectedTabId } = useContext(TabsContext);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [barStyle, setBarStyle] = useState<CSSProperties>({});
  const barRef = useRef<HTMLDivElement>(null);

  const setBarPos = useCallback((selectedTabId: StringNumberValue) => {
    if (!selectedTabId) return;
    const target: HTMLDivElement | null = document.querySelector(
      `#syncly-selected-tab-${selectedTabId}`,
    );
    if (!target) return;
    if (!scrollRef.current) return;
    const parentRect = target.parentElement?.getBoundingClientRect();
    const scrollRect = scrollRef.current.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    if (!scrollRect) return;
    if (!parentRect) return;
    setBarStyle({
      transform: `translateX(${rect.left - parentRect.left}px)`,
      width: rect.width,
    });

    const offset = document.body.clientWidth - rect.right;
    const curr = scrollRef.current.scrollLeft;
    scrollRef.current.scrollTo({
      left: curr - offset,
    });
  }, []);

  useEffect(() => {
    if (selectedTabId) {
      setBarPos(selectedTabId);
    }
  }, [selectedTabId]);

  if (scrollEnabled)
    return (
      <Style tabSize={tabSize}>
        <HorizontalScrollView hideScrollbar ref={scrollRef}>
          <TabArea
            a="center"
            g={tabSize === 's' ? '4px' : '6px'}
            className="tab-area"
            style={style}
            tabSize={tabSize}
          >
            {tabs.map((tab) => (
              <TabItem
                {...tab}
                key={tab.id}
                onSelectTab={(id) => {
                  onSelect?.(id);
                }}
                selected={selectedTabId === tab.id}
                tabStyle={tabStyle}
                tabSize={tabSize}
                tabColor={tabColor}
                setBarPos={setBarPos}
              />
            ))}
          </TabArea>
          <TabSelectedBar ref={barRef} tabColor={tabColor} style={barStyle} />
        </HorizontalScrollView>
      </Style>
    );

  return (
    <Style tabSize={tabSize} ref={scrollRef}>
      <TabArea
        a="center"
        g={tabSize === 's' ? '4px' : '6px'}
        j="space-between"
        className="tab-area"
        style={style}
        tabSize={tabSize}
      >
        {tabs.map((tab) => (
          <TabItem
            {...tab}
            key={tab.id}
            onSelectTab={(id) => {
              onSelect?.(id);
            }}
            selected={selectedTabId === tab.id}
            tabStyle={tabStyle}
            tabSize={tabSize}
            tabColor={tabColor}
            setBarPos={setBarPos}
          />
        ))}
      </TabArea>
      <TabSelectedBar ref={barRef} tabColor={tabColor} style={barStyle} />
    </Style>
  );
};

type TabItemT = {
  selected: boolean;
  tabStyle?: ComponentType<TabItemStyleProps & HTMLAttributes<HTMLDivElement>>;
  onSelectTab: (id: StringNumberValue) => void;
  setBarPos?: (id: StringNumberValue) => void;
} & Tab &
  TabCustomT;

const TabItem = ({
  selected,
  tabStyle,
  id,
  text,
  count,
  tabSize = 's',
  tabColor = 'default',
  onSelectTab,
  setBarPos,
}: TabItemT) => {
  const Wrapper = useMemo(() => {
    return tabStyle || DefaultTabItemStyle;
  }, [tabStyle]);

  return (
    <Wrapper
      id={`syncly-selected-tab-${id}`}
      onClick={() => {
        onSelectTab(id);
      }}
      selected={selected}
      tabSize={tabSize}
      tabColor={tabColor}
    >
      <Flex g={8}>
        {typeof text === 'string' ? <Typo type="label2">{text}</Typo> : text}
        {typeof count === 'number' && (
          <Typo type="label2" weight="r">
            {' '}
            {commaNumber(count || 0)}
          </Typo>
        )}
      </Flex>
    </Wrapper>
  );
};

export type TabItemStyleProps = {
  selected: boolean;
} & TabCustomT;

const Style = styled(Flex)<TabCustomT>`
  position: relative;
  ${({ tabSize }) => {
    switch (tabSize) {
      case 'm':
        return css`
          border-bottom: 1px solid ${(props) => props.theme.colors.gray300};
        `;
      case 's':
        return css`
          border-bottom: 1px solid ${(props) => props.theme.colors.gray300};
        `;
      default:
        return css``;
    }
  }}
`;

export const DefaultTabItemStyle = styled.div<TabItemStyleProps>`
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
  color: ${(props) => props.theme.colors.gray500};
  transition: color 0.2s;
  cursor: pointer;
  justify-content: center;
  white-space: nowrap;
  word-break: break-word;
  border-bottom: 2px solid transparent;
  font-weight: 600;

  ${({ scrollEnabled }) =>
    !scrollEnabled &&
    css`
      flex: 1;
    `}

  padding: ${(props) => {
    switch (props.tabSize) {
      case 'm':
        return '14px 24px';
      case 's':
        return '8px 10px';
      default:
        return '0px';
    }
  }};

  ${({ selected, tabColor }) =>
    selected &&
    css`
      color: ${(props) => props.theme.colors.gray800};

      ${tabColor === 'primary'
        ? css`
            border-color: ${(props) => props.theme.colors.primary100};
          `
        : css`
            border-color: ${(props) => props.theme.colors.gray700};
          `}
    `}
`;

type TabBarProps = {
  tabColor: 'default' | 'primary';
};

const TabSelectedBar = styled.div<TabBarProps>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 40px;
  height: 2px;
  transition: 0.2s all;
  border-bottom: 2px solid
    ${(props) =>
      props.tabColor === 'primary'
        ? props.theme.colors.primary100
        : props.theme.colors.gray700};
`;

const TabArea = styled(Flex)<TabCustomT>`
  position: relative;
  width: 100%;
`;

export default memo(TabHeader);
